import React, { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { sv } from 'date-fns/locale';
import { FrontPageState } from '../../../reducer';
import { RemoteOrganizer } from '../../../../types';
import Activity from '../../../domain/Activity';
import { striptags } from '../../../../utils';
import TopBlockErrorBoundary from './TopBlockErrorBoundary';

function NotLoggedInHeader(): ReactElement {
  return (
    <h3 className="feed-title">
      <span className="aligner-middle" />
      <span className="media-feed-title-part">Aktiviteter och utbildningar&nbsp;</span>
      <span className="media-feed-title-part font-light" />
    </h3>
  );
}

function NoActivitiesMessage(): ReactElement {
  return (
    <div id="activity-block">
      <div className="activity-feed-item no-activities">
        <div className="activity-item-content">
          <div className="activity-item-text">
            Det finns inga kommande aktiviteter i Svenska Kryssarklubben just nu, men det läggs in nya aktiviteter löpande så kom snart tillbaka igen!
          </div>
        </div>
      </div>
    </div>
  );
}

type Section = 'sxkActivities' | 'groupActivities';

export default function ActivitiesBlock(): ReactElement {
  const [section, setSection] = useState<Section>('sxkActivities');
  const { activities, isLoading } = useSelector((state: FrontPageState) => ({
    activities: state.activities,
    isLoading: state.isLoading.indexOf('activities') !== -1,
  }));

  function organizer(id: number): RemoteOrganizer | null {
    for (const o of activities.organizers) {
      if (o.id === id) {
        return o;
      }
    }
    return null;
  }

  const uniqueOrganizers = new Set<number>(activities.groupActivities.map((a) => a.organizer).filter((o) => !!o));
  const hasGroupActivities = activities.groupActivities.length > 0;
  const current: Activity[] = (activities[section] || []).slice(0, 5);
  const title = (uniqueOrganizers.size == 1 && organizer(uniqueOrganizers.values().next() as unknown as number)?.name) || 'Mina kretsar';
  return (
    <TopBlockErrorBoundary name="ActivitiesBlock" title="Aktiviteter och utbildningar" message="Misslyckades ladda aktiviteter">
      <div id="intro-activities" className={`intro-segment section-part-third ${hasGroupActivities && 'member'}`}>
        <div className="activity-feed box-feed media-feed news-feed">
          <div className="media-feed-title page-title-box">
            <div className="media-feed-title-wrap page-title-box-inner content-bg">
              <div className="member-feed-header">
                <div className="member-feed-title">
                  <NotLoggedInHeader />
                </div>
                {hasGroupActivities && (
                  <div className="member-feed-toggle-buttons">
                    <div
                      id="toggle-activities-member"
                      className={`toggle-activities-button ${section === 'groupActivities' && 'active'}`}
                      onClick={() => {
                        setSection('groupActivities');
                      }}
                    >
                      {title}
                    </div>
                    {activities.sxkActivities.length > 0 && (
                      <div
                        id="toggle-activities-sxk"
                        className={`toggle-activities-button ${section === 'sxkActivities' && 'active'}`}
                        onClick={() => {
                          setSection('sxkActivities');
                        }}
                      >
                        Andra kretsar
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="activity-feed-content section-content">
            {isLoading && (
              <div className="media-feed-loader">
                <div className="cssload-container">
                  <ul className="cssload-flex-container">
                    <li>
                      <span className="cssload-loading" />
                    </li>
                  </ul>
                </div>
              </div>
            )}
            {!isLoading && activities.sxkActivities.length === 0 && activities.groupActivities.length === 0 && <NoActivitiesMessage />}
            {current.length > 0 && (
              <div id="activity-block">
                {current.map((activity) => (
                  <div key={activity.id} className="activity-feed-item">
                    <a href={activity.link}>
                      <div className="activity-item-content">
                        <div className="activity-item-date">{format(parseISO(activity.date), 'd LLL', { locale: sv })}</div>
                        <div className="activity-item-text">
                          <div className="activity-item-title">{activity.title}</div>
                          <div className="activity-item-info">{striptags(activity.description)}</div>
                          <div className="activity-item-footer">
                            <div className="activity-item-organizer activity-footer-item">{organizer(activity.organizer)?.name}</div>
                            <div className="activity-item-category activity-footer-item">{activity.type}</div>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="activities-feed-link">
          <a className="btn btn-default btn-feed" href="/aktiviteter">
            Fler aktiviteter och utbildningar
          </a>
        </div>
      </div>
    </TopBlockErrorBoundary>
  );
}
